import { ImageZnglyLogoRounded } from '@/assets/images';
import QRCodeStyling, { type Options } from 'qr-code-styling';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';

type ForwardedRefProps = {
  qrCode: QRCodeStyling;
};

type Props = Options & {
  style?: React.CSSProperties;
  queryParams?: Record<string, string>;
};

const QrCode = (props: Props, forwardedRef: React.ForwardedRef<ForwardedRefProps>) => {
  const divRef = useRef<HTMLDivElement>(null);
  const qrCode = useRef<QRCodeStyling>(new QRCodeStyling());

  const options = useMemo<Options>(() => {
    const opts: Options = {
      width: 300,
      height: 300,
      imageOptions: {
        crossOrigin: 'anonymous',
        margin: 2,
        imageSize: 0.4,
      },
      qrOptions: {
        errorCorrectionLevel: 'Q',
      },
      dotsOptions: {
        type: 'extra-rounded',
        color: '#000000',
      },
      cornersDotOptions: {
        // type: 'dot',
        color: '#0f8a4d',
      },
      cornersSquareOptions: {
        type: 'extra-rounded',
        color: '#e31c3d',
      },
      type: 'svg',
      margin: 0,
      ...props,
    };

    if (!opts?.image) {
      opts.image = ImageZnglyLogoRounded;
    }

    // append utm_source to url if it doesn't exist
    const href = !!opts?.data ? opts.data : window.location.href;
    if (href.match(/^(https?:\/\/)/) !== null) {
      const url = new URL(href);
      url.searchParams.set('utm_source', 'zn_qrcode');
      url.searchParams.set('utm_medium', 'qr_code');

      if (props?.queryParams) {
        Object.entries(props.queryParams).forEach(([key, value]) => {
          url.searchParams.set(key, value);
        });
      }

      opts.data = `${url.origin}${url.pathname}?${url.searchParams.toString()}`;
    }

    return opts;
  }, [JSON.stringify(props)]);

  useEffect(() => {
    if (divRef.current) {
      qrCode.current.append(divRef.current);
    }
  }, [qrCode.current, divRef]);

  useEffect(() => {
    if (!qrCode) return;
    qrCode.current.update(options);
  }, [qrCode.current, options]);

  useImperativeHandle(forwardedRef, () => ({ qrCode: qrCode.current }), [qrCode.current]);

  return (
    <div
      className="zngly-qr-code-wrapper"
      ref={divRef}
      style={{
        border: '4px solid black',
        borderRadius: '8px',
        width: options?.width ?? '100%',
        height: options?.height ?? '100%',
        ...props?.style,
      }}
    >
      {/* canvas element will be appended here */}
    </div>
  );
};

const useQrCodeRef = () => {
  return useRef<ForwardedRefProps>(null);
};
const QrCodeWithRef = forwardRef(QrCode);

export { QrCodeWithRef as QrCode, useQrCodeRef };

export default QrCodeWithRef;
